import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Target, Pencil, Trash2, DollarSign, TrendingUp, Percent, Wallet } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { SalespersonFilter } from "@/components/dashboard/SalespersonFilter";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { GoalFormModal } from "@/components/goals/GoalFormModal";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/StateViews";
import { fmtBRL, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

const CUR_MONTH = new Date().toISOString().slice(0, 7);

export default function Metas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();

  const [month, setMonth] = useState(CUR_MONTH);
  const [salespersonId, setSalespersonId] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: spData } = useQuery({
    queryKey: ["salespeople", "month"],
    queryFn: () => api.get("/salespeople", { params: { period: "month" } }).then((r) => r.data),
  });
  const salespeople = spData?.salespeople || [];

  const params = { month, salesperson_id: salespersonId === "all" ? undefined : salespersonId };
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["goals", params],
    queryFn: () => api.get("/goals", { params }).then((r) => r.data),
    keepPreviousData: true,
    enabled: /^\d{4}-(0[1-9]|1[0-2])$/.test(month),
  });

  const invalidateAll = () => {
    ["goals", "dashboard", "salespeople", "ranking", "raiox"].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] })
    );
  };

  const createMut = useMutation({
    mutationFn: (payload) => api.post("/goals", payload).then((r) => r.data),
    onSuccess: () => { toast.success("Meta criada."); invalidateAll(); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/goals/${id}`, payload).then((r) => r.data),
    onSuccess: () => { toast.success("Meta atualizada."); invalidateAll(); },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/goals/${id}`).then((r) => r.data),
    onSuccess: () => { toast.success("Meta excluída."); invalidateAll(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (g) => { setEditing(g); setModalOpen(true); };
  const handleSubmit = (payload) =>
    editing ? updateMut.mutateAsync({ id: editing.id, payload }) : createMut.mutateAsync(payload);

  const items = data?.items || [];
  const summary = data?.summary;

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            <Target className="h-7 w-7 text-primary" />
            Metas
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Meta × Realizado × Atingimento × Ritmo</p>
        </div>
        {isAdmin && (
          <Button data-testid="new-goal-button" onClick={openNew} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" /> Nova meta
          </Button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          type="month"
          data-testid="goal-month-filter"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="h-10 w-[180px] bg-card"
        />
        <SalespersonFilter value={salespersonId} onChange={setSalespersonId} options={salespeople} />
      </div>

      {isLoading && <LoadingState label="Carregando metas..." />}
      {isError && <ErrorState message={formatApiErrorDetail(error?.response?.data?.detail)} onRetry={refetch} />}

      {data && !isError && (
        <div className={isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard testId="goal-kpi-meta" title="Meta total" value={fmtBRL(summary.meta_total)} icon={Target} />
            <KpiCard testId="goal-kpi-realizado" title="Realizado total" value={fmtBRL(summary.realizado_total)} icon={DollarSign} />
            <KpiCard testId="goal-kpi-atingimento" title="Atingimento geral" value={fmtPct(summary.atingimento_geral)} icon={Percent}>
              <Progress value={Math.min(summary.atingimento_geral, 100)} className="h-2" />
            </KpiCard>
            <KpiCard testId="goal-kpi-falta" title="Falta" value={fmtBRL(summary.falta_total)} icon={Wallet} />
          </div>

          <div className="mt-6">
            {items.length === 0 ? (
              <EmptyState message="Nenhuma meta cadastrada para este mês." />
            ) : (
              <div className="overflow-x-auto rounded-md border border-border" data-testid="goals-table">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Vendedor</TableHead>
                      <TableHead className="text-right">Meta</TableHead>
                      <TableHead className="text-right">Realizado</TableHead>
                      <TableHead className="w-[160px]">Atingimento</TableHead>
                      <TableHead className="text-right">Esperado</TableHead>
                      <TableHead className="text-right">Diferença</TableHead>
                      <TableHead className="text-right">Falta</TableHead>
                      {isAdmin && <TableHead className="text-right">Ações</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((g) => (
                      <TableRow key={g.id} data-testid={`goal-row-${g.id}`}>
                        <TableCell className="font-medium">{g.salesperson_name}</TableCell>
                        <TableCell className="text-right font-num">{fmtBRL(g.meta)}</TableCell>
                        <TableCell className="text-right font-num">{fmtBRL(g.realizado)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={Math.min(g.atingimento, 100)} className="h-1.5" />
                            <span className="w-12 shrink-0 text-right font-num text-xs text-muted-foreground">{fmtPct(g.atingimento)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-num text-muted-foreground">{fmtPct(g.esperado_pct)}</TableCell>
                        <TableCell className="text-right">
                          <span className={cn("inline-flex items-center gap-1 font-num text-xs font-medium",
                            g.diferenca_pp >= 0 ? "text-[hsl(var(--success))]" : "text-destructive")}>
                            <TrendingUp className="h-3 w-3" />
                            {g.diferenca_pp > 0 ? "+" : ""}{g.diferenca_pp.toFixed(1)}pp
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-num text-muted-foreground">{fmtBRL(g.falta)}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button data-testid={`edit-goal-${g.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(g)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`delete-goal-${g.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteTarget(g)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <GoalFormModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          goal={editing}
          salespeople={salespeople}
          defaultMonth={month}
          onSubmit={handleSubmit}
          submitting={createMut.isPending || updateMut.isPending}
        />
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-goal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir meta?</AlertDialogTitle>
            <AlertDialogDescription>
              A meta será excluída (exclusão lógica) e deixará de contar nos indicadores. O histórico é preservado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-goal"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); }}
            >
              Confirmar exclusão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
